import { z } from 'zod';

export const MotorKindSchema = z.enum(['stepper', 'dc']);

export const MotorSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  kind: MotorKindSchema,
  // FK to a Board doc — the master uses board.can_id (espId) to route the
  // motor command, and channel_index (mIdx) to address the motor port on
  // that board. Null when the motor is unassigned (not yet wired to a board);
  // it then sits in the canvas "Unassigned" lane and can't be commanded.
  board_id: z.string().min(1).nullable(),
  channel_index: z.number().int().min(0).max(15),
  subsystem_id: z.string().nullable(),
  // Tag refs for limit switches associated with this motor (resolved against
  // the project's tags collection — the lss[] from MFVB source).
  limit_switch_tag_ids: z.array(z.string()),
  // Stepper params (also used for belt / dc-step modes). Required for
  // stepper, stored as 0 / [] on dc motors.
  max_steps: z.number().int().nonnegative(),
  max_speed: z.number().int().nonnegative(),
  valid_steps_breakpoints: z.array(z.number().int().nonnegative()),
  // DC-only — null for steppers.
  pwm_freq_hz: z.number().int().positive().nullable(),
  reverse_polarity: z.boolean(),
  // Plan 22 — calibration state (manually entered until firmware contract lands).
  // current_position is step count from home; null means uncalibrated/unknown.
  current_position: z.number().int().nullable(),
  // ms timestamp of the last "mark home" or "set position" event.
  last_calibrated_ms: z.number().int().nullable(),
  // logical zero offset; useful when re-homing.
  home_offset: z.number().int(),
  // Soft motion limits in steps; null = unbounded.
  soft_limit_min: z.number().int().nullable(),
  soft_limit_max: z.number().int().nullable(),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateMotorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  kind: MotorKindSchema,
  board_id: z.string().min(1, 'board_id is required'),
  channel_index: z.number().int().min(0).max(15),
  subsystem_id: z.string().nullable().optional(),
  limit_switch_tag_ids: z.array(z.string()).optional(),
  max_steps: z.number().int().nonnegative().optional(),
  max_speed: z.number().int().nonnegative().optional(),
  valid_steps_breakpoints: z.array(z.number().int().nonnegative()).optional(),
  pwm_freq_hz: z.number().int().positive().nullable().optional(),
  reverse_polarity: z.boolean().optional(),
});

export const UpdateMotorSchema = z.object({
  name: z.string().min(1).optional(),
  kind: MotorKindSchema.optional(),
  // Nullable so a motor can be detached from its board (→ Unassigned lane).
  board_id: z.string().min(1).nullable().optional(),
  channel_index: z.number().int().min(0).max(15).optional(),
  subsystem_id: z.string().nullable().optional(),
  limit_switch_tag_ids: z.array(z.string()).optional(),
  max_steps: z.number().int().nonnegative().optional(),
  max_speed: z.number().int().nonnegative().optional(),
  valid_steps_breakpoints: z.array(z.number().int().nonnegative()).optional(),
  pwm_freq_hz: z.number().int().positive().nullable().optional(),
  reverse_polarity: z.boolean().optional(),
  // Plan 22 — calibration state, all optional on Update so callers can patch
  // individual fields without supplying the rest.
  current_position: z.number().int().nullable().optional(),
  last_calibrated_ms: z.number().int().nullable().optional(),
  home_offset: z.number().int().optional(),
  soft_limit_min: z.number().int().nullable().optional(),
  soft_limit_max: z.number().int().nullable().optional(),
});

export type ParsedMotor = z.infer<typeof MotorSchema>;
export type ParsedMotorKind = z.infer<typeof MotorKindSchema>;
export type ParsedCreateMotor = z.infer<typeof CreateMotorSchema>;
export type ParsedUpdateMotor = z.infer<typeof UpdateMotorSchema>;
